import React, { useState, useEffect, useCallback } from 'react';
import { airportBody } from '@carboncare/schemas';
import { preflight } from '../../services/api/schemaPreflight';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import apiClient from '../../services/api';
import { useCoordinatePaste } from '../../hooks/useCoordinatePaste';
import PageHeader from '../../components/common/PageHeader';
import {
  StatusSnackbars,
  CountChip,
  usePaginationState,
  useReferenceSearch,
  NoResultsRow,
  SearchBarPaper,
  CrudTablePagination,
  DeleteConfirmDialog,
  PortCodeSkeletonRow,
  EditDeleteActions,
  CoordinatesCaption,
  CrudFormDialog,
  PortSharedFormFields,
  validateDecimal,
} from './_shared';

interface Airport {
  iata_code: string;
  name: string;
  city?: string;
  country?: string;
  country_name?: string;
  lat: number;
  lon: number;
  region?: string;
  created_at?: string;
  updated_at?: string;
}

const AirportsPage: React.FC = () => {
  const [airports, setAirports] = useState<Airport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  const { page, setPage, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePaginationState(20);
  const { search, matchMode, searchProps } = useReferenceSearch(setPage);

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [selectedAirport, setSelectedAirport] = useState<Airport | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [airportToDelete, setAirportToDelete] = useState<Airport | null>(null);

  // Form states
  const [formData, setFormData] = useState<Partial<Airport>>({
    iata_code: '',
    name: '',
    city: '',
    country: '',
    country_name: '',
    lat: undefined,
    lon: undefined,
    region: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Use the custom hook for pasting coordinates
  const handlePasteCoordinates = useCoordinatePaste(setFormData, setFormErrors);

  // Fetch airports
  const fetchAirports = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await apiClient.get('/data-management/airports', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search,
          matchMode,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });

      if (response.data.success) {
        setAirports(response.data.data);
        setTotalCount(response.data.pagination.total);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch airports');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, matchMode]);

  useEffect(() => {
    fetchAirports();
  }, [fetchAirports]);

  const handleOpenDialog = (mode: 'add' | 'edit', airport?: Airport) => {
    setDialogMode(mode);
    if (mode === 'edit' && airport) {
      setSelectedAirport(airport);
      setFormData({
        iata_code: airport.iata_code,
        name: airport.name,
        city: airport.city || '',
        country: airport.country || '',
        country_name: airport.country_name || '',
        lat: airport.lat,
        lon: airport.lon,
        region: airport.region || ''
      });
    } else {
      setSelectedAirport(null);
      setFormData({
        iata_code: '',
        name: '',
        city: '',
        country: '',
        country_name: '',
        lat: undefined,
        lon: undefined,
        region: ''
      });
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAirport(null);
    setFormErrors({});
    setFormData({
      iata_code: '',
      name: '',
      city: '',
      country: '',
      country_name: '',
      lat: undefined,
      lon: undefined,
      region: ''
    });
  };

  const handleFormChange = (field: keyof Airport) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = event.target.value;
    if(field === 'iata_code') {
      value = value.slice(0, 3);
    } else if(field === 'country') {
      value = value.slice(0, 2);
    } else if(field === 'region' || field === 'lat' || field === 'lon') {
      value = value.slice(0, 10);
    }
    const isNumericField = field === 'lat' || field === 'lon';
    let nextValue: string | number | undefined = value;
    if (isNumericField) {
      const parsed = Number.parseFloat(value);
      nextValue = Number.isNaN(parsed) ? undefined : parsed;
    }
    setFormData(prev => ({
      ...prev,
      [field]: nextValue,
    }));

    // Clear error for this field if it exists
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSave = async () => {
    const newErrors: Record<string, string> = {};
    if (!formData.iata_code) newErrors.iata_code = 'IATA Code is required';
    if (!formData.name) newErrors.name = 'Name is required';
    const latError = validateDecimal(formData.lat, 'Latitude');
    const lonError = validateDecimal(formData.lon, 'Longitude');
    if (latError) newErrors.lat = latError;
    if (lonError) newErrors.lon = lonError;

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      return;
    }

    try {
      if (dialogMode === 'add') {
        preflight(airportBody, formData);
        const response = await apiClient.post('/data-management/airports', formData);
        if (response.data.success) {
          setSuccess('Airport added successfully');
          fetchAirports();
        }
      } else {
        const response = await apiClient.put(
          `/data-management/airports/${selectedAirport?.iata_code}`,
          formData
        );
        if (response.data.success) {
          setSuccess('Airport updated successfully');
          fetchAirports();
        }
      }
      handleCloseDialog();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save airport');
    }
  };

  const handleDeleteClick = (airport: Airport) => {
    setAirportToDelete(airport);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!airportToDelete) return;

    try {
      const response = await apiClient.delete(
        `/data-management/airports/${airportToDelete.iata_code}`
      );
      if (response.data.success) {
        setSuccess('Airport deleted successfully');
        fetchAirports();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete airport');
    } finally {
      setDeleteDialogOpen(false);
      setAirportToDelete(null);
    }
  };

  return (
    <Box>
      <StatusSnackbars
        error={error}
        success={success}
        onClearError={() => setError('')}
        onClearSuccess={() => setSuccess('')}
      />

      <PageHeader
        description="View and maintain the airport reference dataset."
        actions={
          <>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchAirports}
              variant="outlined"
            >
              Refresh
            </Button>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => handleOpenDialog('add')}
            >
              Add Airport
            </Button>
          </>
        }
      />



      {/* Search Bar */}
      <SearchBarPaper
        placeholder="Search by IATA, name, city, or country..."
        {...searchProps}
        countSlot={<CountChip loading={loading} count={totalCount} label="airports" />}
      />

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>IATA Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Coordinates</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from({ length: 10 }, (_, i) => `skeleton-${i}`).map((rowKey, idx) => (
                  <PortCodeSkeletonRow key={rowKey} idx={idx} codeWidth={48} />
                ))
              : airports.length === 0
                ? <NoResultsRow colSpan={6} search={search} matchMode={matchMode} />
                : airports.map((airport) => (
                  <TableRow key={airport.iata_code} hover>
                    <TableCell>
                      <Chip label={airport.iata_code} size="small" color="primary" />
                    </TableCell>
                    <TableCell>{airport.name}</TableCell>
                    <TableCell>{airport.city || '-'}</TableCell>
                    <TableCell>{airport.country_name || airport.country || '-'}</TableCell>
                    <TableCell>
                      <CoordinatesCaption lat={airport.lat} lon={airport.lon} />
                    </TableCell>
                    <EditDeleteActions
                      onEdit={() => handleOpenDialog('edit', airport)}
                      onDelete={() => handleDeleteClick(airport)}
                    />
                  </TableRow>
                ))}
          </TableBody>
        </Table>
        <CrudTablePagination
          loading={loading}
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Add/Edit Dialog */}
      <CrudFormDialog
        open={openDialog}
        title={dialogMode === 'add' ? 'Add New Airport' : 'Edit Airport'}
        onClose={handleCloseDialog}
        onSave={handleSave}
        saveLabel={dialogMode === 'add' ? 'Add' : 'Update'}
        cancelButtonProps={{ size: 'small' }}
        saveButtonProps={{ size: 'small' }}
      >
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="IATA Code"
                value={formData.iata_code}
                onChange={handleFormChange('iata_code')}
                disabled={dialogMode === 'edit'}
                required
                inputProps={{ maxLength: 3 }}
                error={!!formErrors.iata_code}
                helperText={formErrors.iata_code}
              />
            </Grid>
            <PortSharedFormFields
              nameValue={formData.name}
              cityValue={formData.city}
              countryValue={formData.country}
              countryNameValue={formData.country_name}
              latValue={formData.lat}
              lonValue={formData.lon}
              regionValue={formData.region}
              nameLabel="Airport Name"
              onNameChange={handleFormChange('name')}
              onCityChange={handleFormChange('city')}
              onCountryChange={handleFormChange('country')}
              onCountryNameChange={handleFormChange('country_name')}
              onLatChange={handleFormChange('lat')}
              onLonChange={handleFormChange('lon')}
              onRegionChange={handleFormChange('region')}
              onPaste={handlePasteCoordinates}
              nameError={formErrors.name}
              latError={formErrors.lat}
              lonError={formErrors.lon}
            />
          </Grid>
      </CrudFormDialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
      >
        Are you sure you want to delete airport "{airportToDelete?.name}" ({airportToDelete?.iata_code})?
      </DeleteConfirmDialog>
    </Box>
  );
};

export default AirportsPage;
